import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Bot, Bolt, Check, Copy, History, LinkIcon, Loader2, MessageCircle, PlusCircle, Send, Share2, Sparkles, ThumbsDown, ThumbsUp, TriangleAlert, X } from 'lucide-react';
import { Drawer } from 'vaul';
import { useAuth } from '../auth/useAuth';
import { archiveAiSession, clearAiGeneratedReaction, createAiAnalyzeRun, createAiMessageRun, fetchAiMessages, fetchAiRun, fetchAiRunEvents, fetchAiSessions, setAiGeneratedReaction, type AiCard, type AiGeneratedReaction, type AiGeneratedReactionTarget, type AiGeneratedReactionValue, type AiMessage, type AiResult, type AiRunCreateResponse, type AiRunEvent, type AiSession } from '../lib/api/ai';
import { ApiError } from '../lib/api/client';
import { fetchNews } from '../lib/api/news';
import type { ApiNewsArticle } from '../types/api';
import './market-context.css';

type StreamState = 'idle' | 'connecting' | 'streaming' | 'complete' | 'error';
type Mode = 'analysis' | 'history';
type AnalysisProgressStep = 'reading' | 'drivers' | 'composing';

interface MarketContextDrawerProps {
  open: boolean;
  symbol: string;
  interval: string;
  onOpenChange(open: boolean): void;
}

interface SessionState {
  sessionId: string | null;
  cards: AiCard[];
  messages: AiMessage[];
  reasoning: AiReasoningPart[];
  reactions: ReactionMap;
  runId: string | null;
  updatedAt: number | null;
}

interface AiReasoningPart {
  id: string;
  text: string;
  done: boolean;
}

interface ActiveAiRun {
  runId: string;
  sessionId: string;
  mode: 'analysis' | 'message';
  symbol: string;
  interval: string;
  updatedAt: number;
}

const defaultCards: AiCard[] = [];
type ReactionMap = Record<string, AiGeneratedReactionValue>;
type ContentBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] };

export function MarketContextDrawer({ open, symbol, interval, onOpenChange }: MarketContextDrawerProps) {
  const { user } = useAuth();
  const activeRunScope = user?.id ?? 'anonymous';
  const sessionKey = `${symbol}:${interval}`;
  const [mode, setMode] = useState<Mode>('analysis');
  const [sessionsByKey, setSessionsByKey] = useState<Record<string, SessionState>>({});
  const [historySessions, setHistorySessions] = useState<AiSession[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [state, setState] = useState<StreamState>('idle');
  const [status, setStatus] = useState('Ready');
  const [analysisStep, setAnalysisStep] = useState<AnalysisProgressStep>('reading');
  const [error, setError] = useState('');
  const [input, setInput] = useState('');
  const [newsArticles, setNewsArticles] = useState<ApiNewsArticle[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLElement | null>(null);
  const streamModeRef = useRef<'analysis' | 'message'>('analysis');
  const selectedSessionIdRef = useRef<string | null>(null);
  const skipLatestRestoreRef = useRef<Set<string>>(new Set());
  const sessionEpochRef = useRef<Record<string, number>>({});

  const current = sessionsByKey[sessionKey] ?? emptySession();
  const isBusy = state === 'connecting' || state === 'streaming';
  const isAnalysisBusy = isBusy && streamModeRef.current === 'analysis';
  const isMessageBusy = isBusy && streamModeRef.current === 'message';
  const showStatus = state === 'error';
  const showAnalysisSkeleton = sessionLoading || (historyLoading && !current.sessionId && !current.cards.length && !current.messages.length);
  const hasSessionContent = current.cards.length > 0 || current.messages.length > 0 || current.reasoning.length > 0;

  const cards = useMemo(() => orderedCards(current.cards), [current.cards]);

  useEffect(() => {
    selectedSessionIdRef.current = current.sessionId;
  }, [current.sessionId]);

  useEffect(() => {
    if (!open) return;
    setMode('analysis');
    void loadHistory();
    void loadRelatedNews();
  }, [open, symbol, interval]);

  useEffect(() => {
    const activeRun = readActiveRun(activeRunScope, sessionKey);
    if (!activeRun || activeRun.symbol !== symbol || activeRun.interval !== interval) return;
    if (current.sessionId && current.sessionId !== activeRun.sessionId) return;
    if (state === 'connecting' || state === 'streaming') return;
    streamModeRef.current = activeRun.mode;
    void attachRun(activeRun);
  }, [activeRunScope, sessionKey, symbol, interval, current.sessionId, state]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  useEffect(() => {
    if (!open || mode !== 'analysis') return;
    requestAnimationFrame(() => {
      if (!scrollRef.current) return;
      scrollRef.current.scrollTop = !isBusy && current.messages.length === 0 ? 0 : scrollRef.current.scrollHeight;
    });
  }, [open, mode, cards.length, current.messages, current.reasoning.length, status, isBusy, sessionLoading, newsArticles.length]);

  async function loadHistory() {
    const epoch = sessionEpoch(sessionEpochRef.current, sessionKey);
    setHistoryLoading(true);
    try {
      const response = await fetchAiSessions({ symbol, interval, limit: 20 });
      if (sessionEpoch(sessionEpochRef.current, sessionKey) !== epoch) return;
      setHistorySessions(response.sessions ?? []);
      const latestToday = response.sessions?.find((session) => isToday(session.updated_at));
      if (latestToday && !skipLatestRestoreRef.current.has(sessionKey)) {
        const existing = sessionsByKey[sessionKey];
        if (existing?.sessionId !== latestToday.id || (!existing.cards.length && !existing.messages.length)) await restoreSession(latestToday);
      }
      else setSessionsByKey((currentSessions) => ({ ...currentSessions, [sessionKey]: emptySession() }));
    } catch {
      // History is secondary; stream analysis should remain usable if history fails.
    } finally {
      setHistoryLoading(false);
    }
  }

  async function loadRelatedNews() {
    setNewsLoading(true);
    try {
      const response = await fetchNews({ symbol, limit: 3 });
      setNewsArticles(response.articles ?? []);
    } catch {
      setNewsArticles([]);
    } finally {
      setNewsLoading(false);
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) setMode('analysis');
    onOpenChange(nextOpen);
  }

  function startNewSession() {
    if (current.sessionId) void archiveAiSession(current.sessionId).catch(() => undefined);
    abortRef.current?.abort();
    bumpSessionEpoch(sessionEpochRef.current, sessionKey);
    skipLatestRestoreRef.current.add(sessionKey);
    if (current.runId) clearActiveRun(activeRunScope, sessionKey, current.runId);
    setMode('analysis');
    setError('');
    setStatus('');
    setAnalysisStep('reading');
    setState('idle');
    setSessionLoading(false);
    setSessionsByKey((all) => ({ ...all, [sessionKey]: emptySession() }));
  }

  async function startAnalysis() {
    if (isBusy) return;
    streamModeRef.current = 'analysis';
    const previousSessionId = current.sessionId;
    if (previousSessionId) {
      void archiveAiSession(previousSessionId).catch(() => undefined);
    }
    setMode('analysis');
    setError('');
    setStatus('Connecting to Chartify AI');
    setAnalysisStep('reading');
    setState('connecting');
    setSessionsByKey((all) => ({ ...all, [sessionKey]: emptySession() }));
    await createAndAttachRun(() => createAiAnalyzeRun({ symbol, interval }), 'analysis');
  }

  async function askQuestion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const message = input.trim();
    if (!message || isBusy) return;
    setInput('');
    setMode('analysis');
    setError('');
    setState('connecting');
    setStatus('Sending question');
    setAnalysisStep('reading');
    streamModeRef.current = 'message';
    const run = await createAndAttachRun(() => createAiMessageRun({ symbol, interval }, message, current.sessionId), 'message');
    if (run) appendMessage({ id: `local-${Date.now()}`, session_id: run.session_id, role: 'user', content: message, created_at: new Date().toISOString() });
  }

  async function createAndAttachRun(createRun: () => Promise<AiRunCreateResponse>, runMode: ActiveAiRun['mode']): Promise<AiRunCreateResponse | null> {
    let run: AiRunCreateResponse;
    try {
      run = await createRun();
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'AI response failed');
      setStatus('Failed');
      return null;
    }

    const activeRun: ActiveAiRun = { runId: run.run_id, sessionId: run.session_id, mode: runMode, symbol, interval, updatedAt: Date.now() };
    skipLatestRestoreRef.current.delete(sessionKey);
    writeActiveRun(activeRunScope, sessionKey, activeRun);
    setSessionsByKey((all) => {
      const previous = all[sessionKey] ?? emptySession();
      return { ...all, [sessionKey]: { ...previous, sessionId: run.session_id, runId: run.run_id, updatedAt: Date.now() } };
    });
    void attachRun(activeRun);
    return run;
  }

  async function attachRun(activeRun: ActiveAiRun) {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setMode('analysis');
      setState('streaming');
      setStatus(activeRun.mode === 'message' ? 'Answering in background' : 'Analyzing in background');
      let lastEventSequence = 0;
      while (!controller.signal.aborted) {
        const run = await pollRun(activeRun.runId, lastEventSequence, controller.signal, activeRun);
        if (!isSelectedRun(activeRun)) return;
        lastEventSequence = run.lastEventSequence;
        if (run.session_id) {
          setSessionsByKey((all) => {
            const previous = all[sessionKey] ?? emptySession();
            return { ...all, [sessionKey]: { ...previous, sessionId: run.session_id ?? previous.sessionId, runId: run.run_id, updatedAt: Date.now() } };
          });
        }
        if (run.status === 'running') {
          await delay(2000, controller.signal);
          continue;
        }
        if (run.status === 'succeeded') {
          if (run.result) mergeResult({ ...run.result, run_id: run.run_id, session_id: run.session_id });
          if (run.session_id) await loadSessionContent(run.session_id, run.run_id);
          setState('complete');
          setStatus('Complete');
          clearActiveRun(activeRunScope, sessionKey, activeRun.runId);
          void loadHistory();
          return;
        }
        setState('error');
        setError(run.error || 'AI response failed');
        setStatus('Failed');
        clearActiveRun(activeRunScope, sessionKey, activeRun.runId);
        return;
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      setState('error');
      setError(err instanceof Error ? err.message : 'AI response failed');
      setStatus('Failed');
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }

  async function pollRun(runId: string, after: number, signal: AbortSignal, activeRun: ActiveAiRun) {
    try {
      const eventPage = await fetchAiRunEvents(runId, after, signal);
      let lastEventSequence = after;
      for (const event of eventPage.events ?? []) {
        if (!isSelectedRun(activeRun)) return { run_id: runId, status: 'running' as const, lastEventSequence };
        lastEventSequence = Math.max(lastEventSequence, event.sequence);
        handleRunEvent(event);
      }
      const run = await fetchAiRun(runId, signal);
      if (!isSelectedRun(activeRun)) return { run_id: runId, status: 'running' as const, lastEventSequence };
      setError('');
      return { ...run, lastEventSequence };
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        if (!isSelectedRun(activeRun)) return { run_id: runId, status: 'running' as const, lastEventSequence: after };
        setState('streaming');
        setStatus('Reconnecting to AI run');
        await delay(2000, signal);
        return { run_id: runId, status: 'running' as const, lastEventSequence: after };
      }
      throw err;
    }
  }

  function handleRunEvent(event: AiRunEvent) {
    switch (event.event) {
      case 'status': {
        setState('streaming');
        const title = stringValue(event.data.title);
        const message = stringValue(event.data.message);
        setStatus(title || publicStatus(message) || 'Working');
        setAnalysisStep(progressStepForStatus(title || message));
        break;
      }
      case 'skill_start': {
        setState('streaming');
        setStatus('Analyzing market context');
        setAnalysisStep('drivers');
        break;
      }
      case 'reasoning_start':
        setState('streaming');
        updateReasoning(stringValue(event.data.id, 'reasoning'), '', false);
        break;
      case 'reasoning_delta':
        setState('streaming');
        updateReasoning(stringValue(event.data.id, 'reasoning'), stringValue(event.data.text), false);
        break;
      case 'reasoning_end':
        updateReasoning(stringValue(event.data.id, 'reasoning'), '', true);
        break;
      case 'card_delta': {
        setAnalysisStep('composing');
        const result = objectValue<AiResult>(event.data.result);
        if (result) mergeResult(result);
        break;
      }
      case 'final': {
        const result = objectValue<AiResult>(event.data.result);
        if (result) mergeResult(result);
        setStatus('Complete');
        break;
      }
      case 'error':
        setError(stringValue(event.data.message, 'AI response failed'));
        setStatus('Failed');
        break;
      case 'done':
        setStatus('Complete');
        break;
    }
  }

  function mergeResult(result: AiResult) {
    setSessionsByKey((all) => {
      const previous = all[sessionKey] ?? emptySession();
      return {
        ...all,
        [sessionKey]: {
          ...previous,
          sessionId: result.session_id ?? previous.sessionId,
          runId: result.run_id ?? previous.runId,
          cards: Array.isArray(result.cards) ? result.cards : previous.cards,
          updatedAt: Date.now(),
        },
      };
    });
  }

  function appendMessage(message: AiMessage) {
    setSessionsByKey((all) => {
      const previous = all[sessionKey] ?? emptySession();
      return { ...all, [sessionKey]: { ...previous, messages: [...previous.messages, message], updatedAt: Date.now() } };
    });
  }

  function updateReasoning(id: string, textDelta: string, done: boolean) {
    setSessionsByKey((all) => {
      const previous = all[sessionKey] ?? emptySession();
      const index = previous.reasoning.findIndex((part) => part.id === id);
      const nextReasoning = [...previous.reasoning];
      if (index >= 0) {
        const existing = nextReasoning[index];
        if (!existing) return all;
        nextReasoning[index] = { ...existing, text: textDelta || existing.text, done: done || existing.done };
      } else {
        nextReasoning.push({ id, text: textDelta, done });
      }
      return { ...all, [sessionKey]: { ...previous, reasoning: nextReasoning, updatedAt: Date.now() } };
    });
  }

  async function restoreSession(session: AiSession) {
    abortRef.current?.abort();
    const epoch = bumpSessionEpoch(sessionEpochRef.current, sessionKey);
    skipLatestRestoreRef.current.delete(sessionKey);
    setMode('analysis');
    setError('');
    setState('idle');
    setStatus('');
    setSessionLoading(true);
    setSessionsByKey((all) => ({
      ...all,
      [sessionKey]: { ...emptySession(), sessionId: session.id, updatedAt: Date.parse(session.updated_at) || Date.now() },
    }));
    try {
      await loadSessionContent(session.id, session.last_run_id ?? null, Date.parse(session.updated_at) || Date.now(), epoch);
    } catch (err) {
      setState('error');
      setError(err instanceof Error ? err.message : 'Failed to restore session');
    } finally {
      setSessionLoading(false);
    }
  }

  function isSelectedRun(activeRun: ActiveAiRun) {
    const selectedSessionId = selectedSessionIdRef.current;
    return !selectedSessionId || selectedSessionId === activeRun.sessionId;
  }

  async function loadSessionContent(sessionId: string, runId: string | null, updatedAt = Date.now(), expectedEpoch?: number) {
    const response = await fetchAiMessages(sessionId, 50);
    if (expectedEpoch !== undefined && sessionEpoch(sessionEpochRef.current, sessionKey) !== expectedEpoch) return;
    const messages = response.messages ?? [];
    const cards = response.cards ?? [];
    if (messages.length === 0 && cards.length === 0) {
      setSessionsByKey((all) => ({ ...all, [sessionKey]: emptySession() }));
      return;
    }
    setSessionsByKey((all) => ({
      ...all,
      [sessionKey]: {
        ...emptySession(),
        sessionId,
        cards,
        messages,
        reactions: reactionMap(response.reactions ?? []),
        updatedAt,
        runId,
      },
    }));
  }

  async function updateReaction(target: AiGeneratedReactionTarget, nextReaction: AiGeneratedReactionValue | null) {
    const key = reactionKey(target);
    const previousReaction = current.reactions[key] ?? null;
    setSessionsByKey((all) => setReactionForSession(all, sessionKey, key, nextReaction));
    try {
      if (nextReaction) await setAiGeneratedReaction(target, nextReaction);
      else await clearAiGeneratedReaction(target);
    } catch (err) {
      setSessionsByKey((all) => setReactionForSession(all, sessionKey, key, previousReaction));
      setError(err instanceof Error ? err.message : 'Failed to save reaction');
    }
  }

  return (
    <Drawer.Root open={open} onOpenChange={handleOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay className="market-context-overlay" />
        <Drawer.Content className="market-context-drawer" aria-describedby={undefined}>
          <div className="market-context-handle" aria-hidden="true" />
          <header className="market-context-header">
            <div>
              <Drawer.Title className="market-context-title">{symbol} · {formatInterval(interval)}</Drawer.Title>
              {mode === 'analysis' && current.updatedAt && hasSessionContent && <p className="market-context-subtitle">{formatSessionTimestamp(current.updatedAt)}</p>}
            </div>
            <div className="market-context-header-actions">
              <button type="button" className="market-context-icon-button" aria-label="Start new AI session" onClick={startNewSession}><PlusCircle size={18} /></button>
              <button type="button" className="market-context-icon-button" aria-label="Session history" onClick={() => setMode(mode === 'history' ? 'analysis' : 'history')}><History size={18} /></button>
              <button type="button" className="market-context-icon-button" aria-label="Close market context" onClick={() => handleOpenChange(false)}><X size={18} /></button>
            </div>
          </header>

          {showStatus && (
            <div className="market-context-toolbar">
              <span className={`market-context-status ${state}`}>{status}</span>
            </div>
          )}

          <main className="market-context-scroll" ref={scrollRef}>
            {mode === 'history' ? (
                <HistoryPanel loading={historyLoading} sessions={historySessions} onRestore={(session) => void restoreSession(session)} />
            ) : (
              <>
                {error && <div className="market-context-error"><TriangleAlert size={16} />{error}</div>}
                {showAnalysisSkeleton ? <AnalysisSkeleton /> : isAnalysisBusy ? <AnalysisProgress status={status} reasoning={current.reasoning} step={analysisStep} /> : cards.length > 0 ? <CardList cards={cards} sessionId={current.sessionId} reactions={current.reactions} onReactionChange={(target, reaction) => void updateReaction(target, reaction)} active={false} persistActions /> : isBusy ? <AnalysisSkeleton /> : <EmptyState onAnalyze={() => void startAnalysis()} />}
                {newsArticles.length > 0 ? <RelatedNews articles={newsArticles.slice(0, 3)} /> : newsLoading ? <RelatedNewsSkeleton /> : null}
                {!sessionLoading && current.messages.length > 0 && <MessageList messages={current.messages} sessionId={current.sessionId} reactions={current.reactions} onReactionChange={(target, reaction) => void updateReaction(target, reaction)} />}
                {isMessageBusy && <ThinkingMessage status={status} reasoning={current.reasoning} />}
              </>
            )}
          </main>

          {mode === 'analysis' && !sessionLoading && (
            <form className="market-context-input-row" onSubmit={(event) => void askQuestion(event)}>
              <label className="sr-only" htmlFor="market-context-question">Ask Chartify AI about this market</label>
              <input id="market-context-question" value={input} disabled={isBusy} placeholder={`Ask about ${symbol}...`} onChange={(event) => setInput(event.target.value)} />
              <button type="submit" disabled={isBusy || !input.trim()} aria-label="Send question"><Send size={18} /></button>
            </form>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

function RelatedNews({ articles }: { articles: ApiNewsArticle[] }) {
  return (
    <section className="market-context-news" aria-label="Related news">
      <div className="market-context-section-heading">Related to this symbol</div>
      {articles.map((article) => (
        <a key={article.id} className="market-context-news-card" href={article.url} target="_blank" rel="noreferrer">
          <span>
            <small>{article.source_name || article.attribution || 'Source'} · {relativeTime(article.published_at)}</small>
            <strong>{article.title}</strong>
            {article.summary && <em>{article.summary}</em>}
          </span>
          {article.image_url && <img src={article.image_url} alt="" loading="lazy" />}
        </a>
      ))}
    </section>
  );
}

function CardList({ cards, sessionId, reactions, onReactionChange, active = false, persistActions = true }: { cards: AiCard[]; sessionId: string | null; reactions: ReactionMap; onReactionChange(target: AiGeneratedReactionTarget, reaction: AiGeneratedReactionValue | null): void; active?: boolean; persistActions?: boolean }) {
  return <section className="market-context-card-stack">{cards.map((card) => <MarketContextCard key={card.id} card={card} sessionId={sessionId} reaction={sessionId && persistActions ? reactions[reactionKey({ target_type: 'card', session_id: sessionId, card_key: card.id })] ?? null : null} onReactionChange={onReactionChange} active={active} persistActions={persistActions} />)}</section>;
}

function MarketContextCard({ card, sessionId, reaction, onReactionChange, active = false, persistActions = true }: { card: AiCard; sessionId: string | null; reaction: AiGeneratedReactionValue | null; onReactionChange(target: AiGeneratedReactionTarget, reaction: AiGeneratedReactionValue | null): void; active?: boolean; persistActions?: boolean }) {
  const meta = cardMeta(card.id);
  const Icon = meta.icon;
  const text = card.content || card.title;
  const progressStatus = active ? card.status ?? 'streaming' : 'complete';
  const target: AiGeneratedReactionTarget | null = sessionId && persistActions ? { target_type: 'card', session_id: sessionId, card_key: card.id } : null;
  return (
    <article className={`market-context-card ${meta.tone} ${progressStatus}`}>
      <div className="market-context-card-heading">
        <span><Icon size={15} /><h3>{meta.label}</h3></span>
        {progressStatus === 'streaming' && <span className="market-context-card-live" aria-label="Still updating" />}
      </div>
      {progressStatus === 'pending' ? <PendingCardContent /> : <div className={progressStatus === 'streaming' ? 'market-context-card-draft' : undefined}><CardContent text={text} compactList={card.id === 'sources'} citations={card.citations} /></div>}
      {card.id !== 'sources' && card.citations && card.citations.length > 0 && (
        <div className="market-context-citations">
          {card.citations.slice(0, 4).map((citation, index) => <a key={`${citation}-${index}`} href={citation} target="_blank" rel="noreferrer">Source {index + 1}</a>)}
        </div>
      )}
      {progressStatus !== 'pending' && <GeneratedActions title={meta.label} text={text} target={target} reaction={reaction} onReactionChange={onReactionChange} />}
    </article>
  );
}

function PendingCardContent() {
  return (
    <div className="market-context-pending-card" aria-label="Waiting for card content">
      <span />
      <span />
      <span />
    </div>
  );
}

function AnalysisProgress({ status, reasoning, step }: { status: string; reasoning: AiReasoningPart[]; step: AnalysisProgressStep }) {
  const title = latestReasoningTitle(reasoning) || publicStatus(status) || 'Working';
  const activeIndex = progressStepIndex(step);
  return (
    <section className="market-context-analysis-progress hero" aria-live="polite">
      <div className="market-context-progress-orb"><Loader2 className="market-context-spin" size={18} /></div>
      <div className="market-context-progress-copy">
        <span>
          <strong>Building market analysis</strong>
          <small>{title}</small>
        </span>
        <ol className="market-context-progress-steps">
          {['Reading chart', 'Checking market drivers', 'Composing cards'].map((label, index) => (
            <li key={label} className={index === activeIndex ? 'active' : index < activeIndex ? 'complete' : undefined}>{label}</li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function HistoryPanel({ loading, sessions, onRestore }: { loading: boolean; sessions: AiSession[]; onRestore(session: AiSession): void }) {
  if (loading) return <HistorySkeleton />;
  if (sessions.length === 0) return <div className="market-context-empty">No AI sessions for this chart yet.</div>;
  return (
    <section className="market-context-history-list">
      {sessions.map((session) => {
        const activityCount = session.message_count;
        return (
          <button key={session.id} type="button" onClick={() => onRestore(session)}>
            <span><strong>{session.symbol || 'Chart'} · {formatInterval(session.interval)}</strong><small>{activityCount} messages · {formatDate(session.updated_at)}</small></span>
            <MessageCircle size={16} />
          </button>
        );
      })}
    </section>
  );
}

function HistorySkeleton() {
  return (
    <section className="market-context-history-list" aria-label="Loading history">
      {Array.from({ length: 4 }, (_, index) => <div key={index} className="market-context-history-skeleton" />)}
    </section>
  );
}

function EmptyState({ onAnalyze }: { onAnalyze(): void }) {
  return (
    <section className="market-context-empty market-context-empty-hero">
      <Sparkles size={24} />
      <strong>Analyze current chart context</strong>
      <p>Get a concise market read, key drivers, watch points, and sources.</p>
      <button type="button" onClick={onAnalyze}>Start market analysis</button>
    </section>
  );
}

function AnalysisSkeleton() {
  return (
    <section className="market-context-skeleton-card" aria-label="Loading analysis">
      <div className="market-context-skeleton-heading" />
      <div className="market-context-skeleton-line wide" />
      <div className="market-context-skeleton-line" />
      <div className="market-context-skeleton-line short" />
    </section>
  );
}

function RelatedNewsSkeleton() {
  return (
    <section className="market-context-news" aria-label="Loading related news">
      <div className="market-context-section-heading">Related to this symbol</div>
      {Array.from({ length: 3 }, (_, index) => <div key={index} className="market-context-news-skeleton" />)}
    </section>
  );
}

function CardContent({ text, compactList = false, citations = [] }: { text: string; compactList?: boolean; citations?: string[] }) {
  const normalizedText = normalizeMarkdownArtifacts(text);
  const blocks = compactList ? sourceBlocks(normalizedText) : contentBlocks(normalizedText);
  return (
    <div className="market-context-card-content">
      {blocks.map((block, index) => {
        if (block.type === 'heading') return <strong key={index}>{renderInlineMarkdown(block.text)}</strong>;
        if (block.type === 'list') return <ul key={index}>{block.items.map((item, itemIndex) => <li key={item}>{compactList ? renderSourceListItem(item, citations[itemIndex]) : renderInlineMarkdown(item)}</li>)}</ul>;
        if (block.type === 'table') return <MarkdownTable key={index} headers={block.headers} rows={block.rows} />;
        return <p key={index}>{renderInlineMarkdown(block.text)}</p>;
      })}
    </div>
  );
}

function MarkdownTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="market-context-table-wrap">
      <table className="market-context-table">
        <thead><tr>{headers.map((header) => <th key={header}>{renderInlineMarkdown(header)}</th>)}</tr></thead>
        <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{headers.map((_, cellIndex) => <td key={cellIndex}>{renderInlineMarkdown(row[cellIndex] ?? '')}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function normalizeMarkdownArtifacts(text: string) {
  return text
    .replace(/(Dựa trên nến:)/gi, '\n**$1** ')
    .replace(/(Dựa trên tin tức:)/gi, '\n**$1**\n')
    .replace(/\s+\((\d+)\)\s+/g, '\n- ')
    .replace(/Chartify\s+ClickHouse/gi, 'market data')
    .replace(/Chartify\s+Postgres/gi, 'market metadata')
    .replace(/\bClickHouse\b/gi, 'market data')
    .replace(/\bPostgres(?:QL)?\b/gi, 'market metadata')
    .replace(/\[([^\]]+)]\s*\((https?:\/\/[^)]+)\)/g, (_, label: string, url: string) => `[${label}](${url.replace(/\s+/g, '')})`)
    .replaceAll('$\\rightarrow$', '→')
    .replaceAll('$|rightarrow$', '→')
    .replaceAll('\\rightarrow', '→')
    .replaceAll('|rightarrow', '→');
}

function sourceBlocks(text: string): ContentBlock[] {
  const items = text.split('\n').map((line) => line.trim().replace(/^[-*]\s+/, '')).filter(Boolean);
  return items.length > 1 ? [{ type: 'list', items }] : contentBlocks(text);
}

function contentBlocks(text: string): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  let paragraph: string[] = [];
  let listItems: string[] = [];

  function flushParagraph() {
    if (paragraph.length === 0) return;
    blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
    paragraph = [];
  }

  function flushList() {
    if (listItems.length === 0) return;
    blocks.push({ type: 'list', items: listItems });
    listItems = [];
  }

  const lines = markdownLines(text);
  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = lines[index] ?? '';
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    const labelMatch = line.match(/^\*\*(Dựa trên (?:nến|tin tức):)\*\*\s*(.*)$/i);
    if (labelMatch?.[1]) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: labelMatch[1] });
      if (labelMatch[2]) paragraph.push(labelMatch[2]);
      continue;
    }
    const table = parseMarkdownTable(lines, index);
    if (table) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'table', headers: table.headers, rows: table.rows });
      index = table.nextIndex - 1;
      continue;
    }
    const listMatch = line.match(/^[-*•]\s+(.+)$/);
    if (listMatch?.[1]) {
      flushParagraph();
      listItems.push(listMatch[1]);
      continue;
    }
    const headingMatch = line.match(/^#{1,4}\s+(.+)$/);
    if (headingMatch?.[1]) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: headingMatch[1] });
      continue;
    }
    flushList();
    paragraph.push(line);
  }

  flushParagraph();
  flushList();
  return blocks.length > 0 ? blocks : [{ type: 'paragraph', text }];
}

function parseMarkdownTable(lines: string[], startIndex: number): { headers: string[]; rows: string[][]; nextIndex: number } | null {
  const header = tableCells(lines[startIndex] ?? '');
  const separator = tableCells(lines[startIndex + 1] ?? '');
  if (header.length < 2 || separator.length !== header.length || !separator.every((cell) => /^:?-{2,}:?$/.test(cell))) return null;
  const rows: string[][] = [];
  let nextIndex = startIndex + 2;
  while (nextIndex < lines.length) {
    const row = tableCells(lines[nextIndex] ?? '');
    if (row.length !== header.length) break;
    rows.push(row);
    nextIndex += 1;
  }
  return rows.length > 0 ? { headers: header, rows, nextIndex } : null;
}

function tableCells(line: string): string[] {
  const trimmed = line.trim();
  if (!trimmed.includes('|')) return [];
  const withoutEdges = trimmed.replace(/^\|/, '').replace(/\|$/, '');
  return withoutEdges.split('|').map((cell) => cell.trim());
}

function markdownLines(text: string) {
  return text.split('\n').flatMap((rawLine) => {
    const trimmed = rawLine.trim();
    if (!trimmed.includes('•')) return [rawLine];
    const items = trimmed.split(/\s*•\s+/).map((item) => item.trim()).filter(Boolean);
    return trimmed.startsWith('•') && items.length > 0 ? items.map((item) => `• ${item}`) : [rawLine];
  });
}

function renderInlineMarkdown(text: string) {
  return text.split(/(\[[^\]]+]\(https?:\/\/[^)]+\)|\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) => {
    const link = part.match(/^\[([^\]]+)]\((https?:\/\/[^)]+)\)$/);
    if (link?.[1] && link[2]) return <a key={index} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>;
    const strong = part.match(/^\*\*(.+)\*\*$/);
    return strong?.[1] ? <strong key={index}>{strong[1]}</strong> : <Fragment key={index}>{part}</Fragment>;
  });
}

function renderSourceListItem(text: string, href?: string) {
  if (!href) return renderInlineMarkdown(text);
  const separatorIndex = text.lastIndexOf(':');
  if (separatorIndex < 0) return <a href={href} target="_blank" rel="noreferrer">{renderInlineMarkdown(text)}</a>;
  const label = text.slice(separatorIndex + 1).trim();
  if (!label) return renderInlineMarkdown(text);
  return <>{renderInlineMarkdown(`${text.slice(0, separatorIndex + 1)} `)}<a href={href} target="_blank" rel="noreferrer">{label}</a></>;
}

function MessageList({ messages, sessionId, reactions, onReactionChange }: { messages: AiMessage[]; sessionId: string | null; reactions: ReactionMap; onReactionChange(target: AiGeneratedReactionTarget, reaction: AiGeneratedReactionValue | null): void }) {
  const visibleMessages = dedupeMessages(messages.filter((message) => (message.role === 'user' || message.role === 'assistant') && !isMachinePayload(message.content)));
  if (visibleMessages.length === 0) return null;
  return (
    <section className="market-context-message-list" aria-label="Session messages">
      {visibleMessages.map((message) => {
        const target: AiGeneratedReactionTarget | null = sessionId && message.role === 'assistant' ? { target_type: 'message', session_id: sessionId, message_id: message.id } : null;
        const reaction = target ? reactions[reactionKey(target)] ?? null : null;
        return (
          <div key={message.id} className={`market-context-message ${message.role}`}>
            {message.role === 'assistant' ? (
              <>
                <CardContent text={message.content} />
                <GeneratedActions title="Assistant message" text={message.content} target={target} reaction={reaction} onReactionChange={onReactionChange} />
              </>
            ) : message.content}
          </div>
        );
      })}
    </section>
  );
}

function GeneratedActions({ title, text, target, reaction, onReactionChange }: { title: string; text: string; target: AiGeneratedReactionTarget | null; reaction: AiGeneratedReactionValue | null; onReactionChange(target: AiGeneratedReactionTarget, reaction: AiGeneratedReactionValue | null): void }) {
  const [copied, setCopied] = useState(false);
  const canReact = Boolean(target);

  async function handleCopy() {
    const ok = await copyGeneratedText(text);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1400);
  }

  async function handleShare() {
    if (navigator.share) {
      try {
        await navigator.share({ title, text });
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }
    await handleCopy();
  }

  function handleReaction(nextReaction: AiGeneratedReactionValue) {
    if (!target) return;
    onReactionChange(target, reaction === nextReaction ? null : nextReaction);
  }

  return (
    <div className="market-context-generated-actions" aria-label="Generated content actions">
      <button type="button" onClick={() => void handleCopy()} aria-label={copied ? 'Copied generated text' : 'Copy generated text'}><span>{copied ? <Check size={14} /> : <Copy size={14} />}</span></button>
      <button type="button" className={reaction === 'like' ? 'active' : undefined} disabled={!canReact} onClick={() => handleReaction('like')} aria-pressed={reaction === 'like'} aria-label="Like generated text"><ThumbsUp size={14} /></button>
      <button type="button" className={reaction === 'dislike' ? 'active' : undefined} disabled={!canReact} onClick={() => handleReaction('dislike')} aria-pressed={reaction === 'dislike'} aria-label="Dislike generated text"><ThumbsDown size={14} /></button>
      <button type="button" onClick={() => void handleShare()} aria-label="Share generated text"><Share2 size={14} /></button>
    </div>
  );
}

function ThinkingMessage({ status, reasoning }: { status: string; reasoning: AiReasoningPart[] }) {
  const title = latestReasoningTitle(reasoning) || publicStatus(status) || 'Thinking';
  return (
    <div className="market-context-thinking" aria-live="polite">
      <span><Loader2 className="market-context-spin" size={15} />{title}</span>
    </div>
  );
}

function dedupeMessages(messages: AiMessage[]): AiMessage[] {
  return messages.filter((message, index) => {
    const previous = messages[index - 1];
    return !previous || previous.role !== message.role || previous.content.trim() !== message.content.trim();
  });
}

function reasoningTitle(text: string): string {
  const content = text.replaceAll('[REDACTED]', '').trim();
  if (!content) return '';
  const boldTitle = content.match(/^\*\*([^*\n]+)\*\*/);
  const title = boldTitle?.[1] ?? content.split(/\r?\n/)[0] ?? '';
  return safeProgressTitle(title.replace(/^Thought:\s*/i, ''));
}

function latestReasoningTitle(reasoning: AiReasoningPart[]): string {
  for (let index = reasoning.length - 1; index >= 0; index -= 1) {
    const part = reasoning[index];
    const title = part ? reasoningTitle(part.text) : '';
    if (title) return title;
  }
  return '';
}

function publicStatus(value: string): string {
  const status = value.trim();
  if (!status) return '';
  if (/^[a-z]+(?:_[a-z0-9]+)+$/.test(status)) return 'Analyzing market context';
  return status;
}

function progressStepForStatus(value: string): AnalysisProgressStep {
  const status = value.toLowerCase();
  if (status.includes('compos') || status.includes('writ') || status.includes('answer') || status.includes('verif') || status.includes('final')) return 'composing';
  if (status.includes('news') || status.includes('driver') || status.includes('search') || status.includes('skill')) return 'drivers';
  return 'reading';
}

function progressStepIndex(step: AnalysisProgressStep): number {
  if (step === 'composing') return 2;
  if (step === 'drivers') return 1;
  return 0;
}

function safeProgressTitle(value: string): string {
  const title = value
    .trim()
    .replace(/\*\*/g, '')
    .replace(/^Title\s*:?\s*/i, '')
    .trim()
    .split(/\s+/)
    .slice(0, 8)
    .join(' ');
  if (!title || title.startsWith('{') || title.startsWith('[') || title.startsWith('```')) return '';
  if (title.toLowerCase() === 'json' || title.toLowerCase() === '```json') return '';
  if (/[{}\[\]]/.test(title)) return '';
  return title;
}

function isMachinePayload(content: string): boolean {
  const trimmed = content.trim();
  if (trimmed.startsWith('{"parts"') || trimmed.startsWith("{'parts'") || trimmed.includes('"tool_name":"final_result"') || trimmed.includes('"tool_name": "final_result"')) return true;
  const payload = parseJSONRecord(content);
  if (!payload) return false;
  const parts = Array.isArray(payload.parts) ? payload.parts : [];
  return parts.some((part) => isRecord(part) && (typeof part.tool_name === 'string' || part.kind === 'request' || part.part_kind === 'tool-return'));
}

function parseJSONRecord(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value) as unknown;
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function objectValue<T>(value: unknown): T | undefined {
  return isRecord(value) ? value as T : undefined;
}

function emptySession(): SessionState {
  return { sessionId: null, cards: defaultCards, messages: [], reasoning: [], reactions: {}, runId: null, updatedAt: null };
}

function sessionEpoch(epochs: Record<string, number>, sessionKey: string): number {
  return epochs[sessionKey] ?? 0;
}

function bumpSessionEpoch(epochs: Record<string, number>, sessionKey: string): number {
  const nextEpoch = sessionEpoch(epochs, sessionKey) + 1;
  epochs[sessionKey] = nextEpoch;
  return nextEpoch;
}

function activeRunKey(scope: string, sessionKey: string): string {
  return `chartify:ai-active-run:${scope}:${sessionKey}`;
}

function readActiveRun(scope: string, sessionKey: string): ActiveAiRun | null {
  try {
    const raw = localStorage.getItem(activeRunKey(scope, sessionKey));
    if (!raw) return null;
    const value = JSON.parse(raw) as unknown;
    if (!isRecord(value) || typeof value.runId !== 'string' || typeof value.sessionId !== 'string') return null;
    if (value.mode !== 'analysis' && value.mode !== 'message') return null;
    if (typeof value.symbol !== 'string' || typeof value.interval !== 'string') return null;
    return { runId: value.runId, sessionId: value.sessionId, mode: value.mode, symbol: value.symbol, interval: value.interval, updatedAt: typeof value.updatedAt === 'number' ? value.updatedAt : Date.now() };
  } catch {
    return null;
  }
}

function writeActiveRun(scope: string, sessionKey: string, run: ActiveAiRun): void {
  localStorage.setItem(activeRunKey(scope, sessionKey), JSON.stringify(run));
}

function clearActiveRun(scope: string, sessionKey: string, runId: string): void {
  const current = readActiveRun(scope, sessionKey);
  if (!current || current.runId === runId) localStorage.removeItem(activeRunKey(scope, sessionKey));
}

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timeout = window.setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      window.clearTimeout(timeout);
      resolve();
    }, { once: true });
  });
}

function reactionMap(reactions: AiGeneratedReaction[]): ReactionMap {
  const map: ReactionMap = {};
  for (const reaction of reactions) map[reactionKey(reaction)] = reaction.reaction;
  return map;
}

function reactionKey(target: AiGeneratedReactionTarget): string {
  return target.target_type === 'message' ? `message:${target.message_id}` : `card:${target.session_id}:${target.card_key}`;
}

function setReactionForSession(sessions: Record<string, SessionState>, sessionKey: string, key: string, reaction: AiGeneratedReactionValue | null) {
  const previous = sessions[sessionKey] ?? emptySession();
  const reactions = { ...previous.reactions };
  if (reaction) reactions[key] = reaction;
  else delete reactions[key];
  return { ...sessions, [sessionKey]: { ...previous, reactions } };
}

async function copyGeneratedText(text: string): Promise<boolean> {
  if (!navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function orderedCards(cards: AiCard[]): AiCard[] {
  const order = ['market_read', 'drivers', 'watch', 'sources'];
  return cards.filter((card) => card.id !== 'sources').sort((a, b) => orderIndex(order, a.id) - orderIndex(order, b.id));
}

function orderIndex(order: string[], value: string): number {
  const index = order.indexOf(value);
  return index === -1 ? order.length : index;
}

function cardMeta(id: string): { label: string; tone: string; icon: typeof Bolt } {
  switch (id) {
    case 'market_read':
      return { label: 'What happened', tone: 'primary', icon: Bolt };
    case 'drivers':
      return { label: 'Why it matters', tone: 'secondary', icon: Bot };
    case 'watch':
      return { label: 'Risk / uncertainty', tone: 'warning', icon: TriangleAlert };
    case 'sources':
      return { label: 'Sources', tone: 'sources', icon: LinkIcon };
    default:
      return { label: 'Context', tone: 'primary', icon: Sparkles };
  }
}

function formatInterval(interval: string): string {
  const map: Record<string, string> = { '1': '1m', '5': '5m', '15': '15m', '30': '30m', '60': '1h', '240': '4h', '1D': '1D', '1W': '1W' };
  return map[interval] ?? interval;
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'recent';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
}

function isToday(value: string): boolean {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
}

function formatSessionTimestamp(value: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function relativeTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'recent';
  const diffMs = Date.now() - date.getTime();
  const hours = Math.max(0, Math.round(diffMs / 3_600_000));
  if (hours < 1) return 'now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
